import { Box, Typography, Button, TextField, CircularProgress, Container } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import API from 'configs/API';
import GalleryTabContent from 'pages/Gallery/components/GalleryTabContent';
import GalleryWrapper from 'pages/Gallery/components/GalleryWrapper';
import Path from 'utils/Route';
import { axiosGet, axiosPost } from 'utils/apis/axios';

const PublicSharePage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState(null);
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState('');

  const sharedUserUuid = shareData?.share_link?.user?.uuid;
  const { data: sharedUserProfile } = useQuery({
    queryKey: [
      API.AUTH.PROFILE_INFO,
      {
        user_uuid: sharedUserUuid,
      },
    ],
    enabled: !!sharedUserUuid,
  });

  const fetchData = async (pwd = null) => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (pwd) {
        res = await axiosPost(API.ARTWORK.SHARE_LINK_ACCESS(uuid), { password: pwd });
      } else {
        res = await axiosGet(API.ARTWORK.SHARE_LINK_ACCESS(uuid));
      }
      if (res.success) {
        setShareData(res.data);
        setShowPasswordForm(false);
      } else if (res.data?.requires_password) {
        setShowPasswordForm(true);
        setError('Password required');
      } else if (res.data?.requires_login) {
        const nextPath = location?.pathname || `/share/${uuid}`;
        navigate(Path.Login, { state: { next: nextPath } });
        setError('Login required');
      } else if (res.data?.detail) {
        // DRF default error shape (e.g., PermissionDenied)
        setError(res.data.detail);
      } else if (res.data?.error) {
        setError(res.data.error);
      }
    } catch (e) {
      // Try to show more specific errors
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 401 && data?.requires_login) {
        const nextPath = location?.pathname || `/share/${uuid}`;
        navigate(Path.Login, { state: { next: nextPath } });
        setError('Login required');
      } else if (status === 401 && data?.requires_password) {
        setShowPasswordForm(true);
        setError('Password required');
      } else if (status === 403) {
        setError(data?.detail || 'You do not have permission to access this share link');
      } else {
        setError('Link is invalid or expired');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [uuid]);

  const handleSubmitPassword = (e) => {
    e.preventDefault();
    fetchData(password);
  };

  const userProfile = {
    ...(sharedUserProfile || {}),
    background: shareData?.share_link?.banner || sharedUserProfile?.background,
    is_owner: false,
  };

  const roleUser = sharedUserProfile?.role || shareData?.share_link?.user?.role;

  if (loading)
    return (
      <Box
        p={4}
        textAlign="center"
      >
        <CircularProgress />
      </Box>
    );

  if (error && !showPasswordForm)
    return (
      <Box
        p={4}
        textAlign="center"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );

  return (
    <Container sx={{ pb: 8 }}>
      {showPasswordForm ? (
        <form onSubmit={handleSubmitPassword}>
          <Typography
            variant="h6"
            mb={2}
          >
            This link is protected. Please enter password:
          </Typography>
          <TextField
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label="Password"
            required
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
          >
            Access
          </Button>
          {error && (
            <Typography
              color="error"
              mt={2}
            >
              {error}
            </Typography>
          )}
        </form>
      ) : (
        <>
          <GalleryWrapper userProfile={userProfile} />
          <GalleryTabContent
            userProfile={userProfile}
            roleUser={roleUser}
            isPublicShare={true}
            publicArtworks={shareData?.artworks}
            publicCollections={shareData?.collections}
            publicExhibitions={shareData?.exhibitions}
            shareType={shareData?.share_link?.share_type}
            userUuid={shareData?.share_link?.user?.uuid}
          />
        </>
      )}
    </Container>
  );
};

export default PublicSharePage;
